import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { dialogAnmelden } from '../lib/dialogVerlauf.js';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  /** Centered dialog instead of a bottom sheet (better on wide screens). */
  variant?: 'sheet' | 'modal';
  /**
   * Tritt zur Seite, solange aus dem Blatt heraus etwas Ganzseitiges offen
   * ist – der Fotoeditor zum Beispiel.
   *
   * Die Ebenen sind als Stapel gedacht, und beim Editor IM Blatt läuft der
   * Stapel andersherum als sonst: Das Blatt liegt über der Werkstatt, der
   * Editor ginge also dahinter auf. Der Zustand des Blattes bleibt dabei
   * stehen – siehe `.is-beiseite` in `global.css`.
   */
  beiseite?: boolean;
}

/** Bottom sheet / modal with backdrop, escape handling and scroll locking. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  actions,
  variant = 'sheet',
  beiseite = false,
}: SheetProps) {
  // Ein `role="dialog"` ohne Beschriftung meldet sich bei einem Screenreader
  // als „Dialog“ und sonst nichts. Der Titel stand bisher in einem schmucklosen
  // `span`, also nirgends, wo eine Vorlesehilfe ihn findet. Als Ueberschrift
  // mit `aria-labelledby` ist er beides: sichtbar und angesagt.
  const titleId = useId();

  // `onClose` ist bei fast jedem Aufrufer eine frisch erzeugte Funktion und
  // aendert damit bei jedem Rendern ihre Kennung. Im Abhaengigkeitsfeld
  // unten wuerde das den Effekt staendig neu starten – und mit ihm jedes Mal
  // einen weiteren Verlaufseintrag anlegen. Ueber eine Referenz bleibt der
  // Effekt an `open` haengen, wo er hingehoert.
  const schliessen = useRef(onClose);
  schliessen.current = onClose;

  // Die Zurueck-Taste des Handys schliesst diesen Dialog, statt aus dem Chat
  // zu springen. Die Buchfuehrung darueber liegt bewusst an einer Stelle fuer
  // die ganze App – warum, steht ausfuehrlich in lib/dialogVerlauf.ts.
  useEffect(() => {
    if (!open) return undefined;
    return dialogAnmelden(() => schliessen.current());
  }, [open]);

  /*
   * Esc steht NICHT hier, sondern in `dialogVerlauf.ts` – siehe dort.
   *
   * Kurz: Ein eigener Hörer je Blatt schliesst bei zwei gestapelten Blättern
   * beide auf einmal. Die Buchführung über „welches liegt oben" gibt es für
   * die Zurück-Taste ohnehin, und die Esc-Taste meint dasselbe.
   */
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const content =
    variant === 'modal' ? (
      /*
       * Der Tipp NEBEN das Fenster schliesst – und das musste hierher.
       *
       * Der Schleier darunter traegt zwar `onClick={onClose}`, kündigt also
       * genau das an. Erreichbar war er nie: `.modal` liegt mit
       * `z-index: 77` über dem Schleier (76) und deckt mit `inset: 0` den
       * ganzen Bildschirm. Jeder Klick daneben landete auf dem Modal selbst
       * und versickerte. Eine tote Bedienung, die sich als lebendige ausgibt.
       *
       * Deshalb der Handler hier, und `stopPropagation` auf der Karte: Ein
       * Klick INS Fenster darf es nicht schliessen.
       */
      <div
        className={beiseite ? 'modal is-beiseite' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        onClick={onClose}
      >
        <div className="modal-card" onClick={(event) => event.stopPropagation()}>
          {(title || actions) && (
            <div className="row row-between" style={{ marginBottom: 'var(--space-3)' }}>
              <h2 id={titleId} className="sheet-title">
                {title}
              </h2>
              {/*
                Der ✕-Rückfall gehört auch hierher.
                Er stand nur im Blatt-Zweig; ein Modal ohne eigene
                Abbrechen-Schaltfläche liess sich auf dem Telefon nur über die
                Zurück-Geste verlassen – und wer die nicht kennt, sitzt fest.
              */}
              {actions ?? (
                <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
                  ✕
                </button>
              )}
            </div>
          )}
          {children}
        </div>
      </div>
    ) : (
      <div
        className={beiseite ? 'sheet is-beiseite' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        <div className="sheet-handle" />
        {(title || actions) && (
          <div className="sheet-header">
            <h2 id={titleId} className="sheet-title">
              {title}
            </h2>
            {actions ?? (
              <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
                ✕
              </button>
            )}
          </div>
        )}
        <div className="sheet-body">{children}</div>
      </div>
    );

  return createPortal(
    <>
      <div className={beiseite ? 'backdrop is-beiseite' : 'backdrop'} onClick={onClose} />
      {content}
    </>,
    document.body,
  );
}
